import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  AlertCircle,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
} from 'lucide-react';
import { UserSubscription, SubscriptionPlan } from '../../types/subscription';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Alert } from '../ui';
import api from '../../lib/api';
import { messageHandler } from '../../services/message-handler.service';

interface LicenseInfoProps {
  subscription: UserSubscription | null;
  plans?: SubscriptionPlan[];
  onCancelSubscription: () => void;
  onReactivateSubscription: () => void;
}

function LicenseInfo({
  subscription,
  plans,
  onCancelSubscription,
  onReactivateSubscription,
}: LicenseInfoProps) {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const queryClient = useQueryClient();

  // Mutation for changing plan
  const changePlanMutation = useMutation({
    mutationFn: async (planId: number) => {
      if (!subscription) return;

      await api.put(`/subscriptions/users/${subscription.id}`, {
        plan_id: planId,
        status: subscription.status,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setSelectedPlan(null);
      messageHandler.success('Plano alterado com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'canceled':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'expired':
        return <AlertTriangle className="h-6 w-6 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'canceled':
        return 'error';
      case 'expired':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (value: string | number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(typeof value === 'string' ? parseFloat(value) : value);
  };

  const handlePlanChange = (plan: SubscriptionPlan) => {
    messageHandler.confirm(
      'Mudar de Plano',
      `Tem certeza que deseja mudar para o plano ${
        plan.name
      }? O valor será alterado para ${formatCurrency(plan.price)} por mês.`,
      () => changePlanMutation.mutate(plan.id)
    );
  };

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Informações da Assinatura</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma assinatura ativa</h3>
            <p className="mt-1 text-sm text-gray-500">
              Você não possui uma assinatura ativa no momento.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Informações da Assinatura</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-700 dark:to-primary-800 rounded-lg shadow-lg text-white">
                <div className="flex items-center space-x-4">
                  {getStatusIcon(subscription.status)}
                  <div>
                    <h4 className="text-2xl font-bold">{subscription.plan_name}</h4>
                    <p className="text-lg opacity-90">{formatCurrency(subscription.price)}/mês</p>
                  </div>
                </div>
                <Badge
                  variant={getStatusBadgeVariant(subscription.status)}
                  className="text-lg px-4 py-2 uppercase font-bold"
                >
                  {subscription.status === 'active'
                    ? 'Ativa'
                    : subscription.status === 'canceled'
                    ? 'Cancelada'
                    : 'Expirada'}
                </Badge>
              </div>

              {subscription.is_trial && subscription.trial_expires_at && (
                <Alert variant="warning" icon={AlertCircle}>
                  <h3 className="text-sm font-medium text-yellow-800">Período de Avaliação</h3>
                  <p className="mt-2 text-sm text-yellow-700">
                    Expira em {new Date(subscription.trial_expires_at).toLocaleDateString()}
                  </p>
                </Alert>
              )}

              {subscription.referred_by && subscription.referred_by_name && (
                <Alert variant="info" icon={Users}>
                  <h3 className="text-sm font-medium text-blue-800">Indicação</h3>
                  <p className="mt-2 text-sm text-blue-700">
                    Você foi indicado por {subscription.referred_by_name}
                    {subscription.discount_applied && subscription.discount_applied > 0 && (
                      <span>
                        {' '}
                        e recebeu um desconto de {formatCurrency(subscription.discount_applied)}
                      </span>
                    )}
                  </p>
                </Alert>
              )}

              <div className="mt-6 space-x-4">
                {subscription.status === 'active' ? (
                  <Button
                    variant="danger"
                    onClick={onCancelSubscription}
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    Cancelar Assinatura
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={onReactivateSubscription}
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    Reativar Assinatura
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {plans && plans.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Planos Disponíveis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {plans.map(plan => {
                  const hasDiscount =
                    'discounted_price' in plan && plan.discounted_price < plan.price;

                  return (
                    <div
                      key={plan.id}
                      className={`p-6 rounded-lg border-2 ${
                        subscription.plan_id === plan.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                          : 'border-gray-200 hover:border-primary-300 dark:border-gray-700'
                      }`}
                    >
                      <h3 className="text-lg font-medium">{plan.name}</h3>
                      <p className="mt-2 text-sm text-gray-500">{plan.description}</p>
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {hasDiscount ? (
                          <div>
                            <p className="text-sm line-through text-gray-500">
                              {formatCurrency(plan.price)}
                            </p>
                            <p className="text-2xl font-bold text-green-600">
                              {formatCurrency(plan.discounted_price)}
                            </p>
                            <p className="text-sm text-gray-500">/mês</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-2xl font-bold">{formatCurrency(plan.price)}</p>
                            <p className="text-sm text-gray-500">/mês</p>
                          </div>
                        )}
                      </div>

                      {subscription.plan_id !== plan.id && (
                        <Button
                          className="mt-4 w-full"
                          onClick={() => handlePlanChange(plan)}
                          disabled={plan.status !== 'active'}
                        >
                          Mudar para este plano
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}

export default LicenseInfo;
